import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import extend from 'lodash-es/extend';
import flatten from 'lodash-es/flatten';
import includes from 'lodash-es/includes';
import pick from 'lodash-es/pick';
import reduce from 'lodash-es/reduce';

import dayjs from '@utils/dayjs-wrapper';
import CONSTANTS, { EVENTS } from '@constants';
import browserHelper from '@utils/browser-helper';
import { EFBUserRole, IFBUserInfo } from '@interfaces';
import { events } from '@utils/events';
import { storage } from '@utils/storage.service';
import { AccountResource } from '@app/shared/modules/resources/account.resource';
import { IFBWindow } from '@app/models/window-namespace';
import { logger } from '@utils/logger.service';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';
import { UserResource } from '@app/shared/modules/resources/user.resource';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserManager {
  // @NB
  // - We set the initial state in BehaviorSubject's constructor
  // - Nobody outside the Store should have access to the BehaviorSubject, because it has the write rights
  // - Writing to state should be handled by specialized Store methods (ex: addTodo, removeTodo, etc)
  // - Create one BehaviorSubject per store entity, for example if you have TodoGroups
  //   create a new BehaviorSubject for it, as well as the observable$, and getters/setters
  private readonly _data = new BehaviorSubject<IFBUserInfo>({} as any);

  // Expose the observable$ part of the _data subject (read only stream)
  data$ = this._data.asObservable();

  isLoggedIn$ = new BehaviorSubject<boolean>(false);
  isCustomerConnected = false;

  constructor(
    @Inject('APP') public APP: string,
    @Inject('Window') private window: IFBWindow,
    private accountResource: AccountResource,
    private translateService: TranslateService,
    private userResource: UserResource
  ) {
    // Listeners
    // Clean up UserManager data when session ended
    events.on(EVENTS.AUTH_SERVICE.END_SESSION, () => {
      this.clean();
    });
  }

  get data(): IFBUserInfo {
    return this._data.getValue();
  }

  get isLoggedIn() {
    return this.isLoggedIn$.getValue();
  }

  clean(): void {
    storage.removeItem('token');
    storage.removeItem('tokenTTL');

    this.isLoggedIn$.next(false);
    this._data.next({} as IFBUserInfo);
  }

  set(newData: object): void {
    this._data.next(extend(this.data, newData));
    this.isLoggedIn$.next(true);

    // Change dayjs locale
    // Use shortened locale otherwise dayjs fails with English
    dayjs.locale(this.translateService.overrideLocale(this.data.locale).substr(0, 2));

    this.setLocale(this.data.locale);
  }

  roleInCompany(data: { roles: EFBUserRole[] }): EFBUserRole | '' {
    if (!data.roles) {
      return '';
    }

    return reduce(
      data.roles,
      (memo, role) => {
        return CONSTANTS.ROLE_HIERARCHY[role] > CONSTANTS.ROLE_HIERARCHY[memo] ? role : memo;
      },
      data.roles![0]
    );
  }

  /**
   * Function accepts any number of arguments, roles to check current user against.
   * Returns true if current user role in current company matches any of roles in arguments
   */
  isAnyRoleInCompany(...roles: (EFBUserRole | string)[]): boolean {
    if (!this.isLoggedIn) {
      return false;
    }

    const userRoleInCompany = this.roleInCompany(pick(this.data, 'roles'));
    if (!userRoleInCompany) {
      return false;
    }

    return includes(flatten(roles), userRoleInCompany);
  }

  /**
   *
   * When checkTheMainRole param passed, function also checks the user.mainRole value,
   * on the mobile each user role is Talent Scout, but mainRole stays
   */
  isCompanyAdmin(checkTheMainRole: boolean = false): boolean {
    const isCAbyMainRole = this.data.mainRole === CONSTANTS.USER_ROLES.COMPANY_ADMIN;
    const isCA = this.isAnyRoleInCompany(CONSTANTS.USER_ROLES.COMPANY_ADMIN as EFBUserRole);

    if (checkTheMainRole) {
      return isCAbyMainRole || isCA;
    } else {
      return isCA;
    }
  }

  isTalentScout(): boolean {
    return this.isAnyRoleInCompany(CONSTANTS.USER_ROLES.TALENT_SCOUT as EFBUserRole);
  }

  isRecruiter(): boolean {
    return this.isAnyRoleInCompany(CONSTANTS.USER_ROLES.RECRUITER as EFBUserRole);
  }

  isUserOfCompany(): boolean {
    return this.isAnyRoleInCompany(EFBUserRole.COMPANY_ADMIN, EFBUserRole.RECRUITER, EFBUserRole.TALENT_SCOUT);
  }

  fetchLoggedIn(): Subject<IFBUserInfo> {
    const subject = new Subject<IFBUserInfo>();

    const setUser = (data: IFBUserInfo) => {
      this.set(data);
      this.setDataLayer();
      this.setLocale(data.locale);
      this.fetchUserDetails().subscribe(() => {
        subject.next(data);
        subject.complete();
      });
    };

    if (this.isLoggedIn) {
      // init service with data if user was logged in
      setUser(this.data);
    } else if (storage.getItem('token')) {
      // fetch user after login
      this.accountResource.get(storage.getItem('token')).subscribe(
        response => setUser(response),
        err => {
          logger.error(err);
          subject.error(err);
          subject.complete();
        }
      );
    }

    return subject;
  }

  // Helpers
  private forceTalentScoutRoleInWrapper(): void {
    if (browserHelper.isInWrapper) {
      this.set({ roles: [CONSTANTS.USER_ROLES.TALENT_SCOUT] });
    }
  }

  private setLocale(locale: string): void {
    // use language stored in the user account, unless it is set to Afrikaans or Aragonese (i.e. need in-context translations)
    if (browserHelper.preferredLanguage !== 'af' && browserHelper.preferredLanguage !== 'an') {
      this.translateService.setLocale(locale);
    }
  }

  private fetchUserDetails(): Observable<IFBUserInfo> {
    return this.userResource.getById(this.data.id).pipe(
      tap(response => {
        this.set(response);
        this.forceTalentScoutRoleInWrapper();
      })
    );
  }

  private setDataLayer(): void {
    // Safely instantiate dataLayer
    const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
    const userType = this.roleInCompany(pick(this.data, 'roles'));
    dataLayer.push({ userType });
  }
}
